import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
import { useLocation, /* useMatch, */ useNavigate } from 'react-router-dom';
import { shouldShowFloorplanV2 } from '@components/floorplan-v2/should-show-floorplan-v2';
import { /* COMPANY_SETTINGS_ROUTE, */ SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContextValue, CompanyClubState } from '@contexts/companyClub/companyClub.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { Club } from '@domain/club';
import { Company } from '@domain/company';
import { useGetCompanyClub } from '@hooks/company-club/use-get-company-club';
import { usePostHog } from '@posthog/react';
import { clearCompanyContext, identifyCompanyGroupProperties } from '@posthog-app/company-identification';
import { shouldShowNewForm } from '@posthog-app/should-show-new-form';
import { useFeatureFlag, useFeatureFlagPayload } from '@posthog-app/use-feature-flag';

import { companyClubEmptyState } from './utils';

export const CompanyClubProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setCompanyAuthId, setCompanyClubId } = useContext(CompanyAuthContext);
  const { isSelectVenueActive, testCompanyIds, externalPlatformEnabledIds } = useContext(FeatureFlagContext);
  const { isAuthenticated, isImpersonated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const posthog = usePostHog();
  const companyId = pathname.split('/')[1];
  const clubSlug = pathname.split('/')[2];

  const isTestVenueCompany = (testCompanyIds as string).split(',').includes(companyId);
  const isExternalPlatformEnabledForCompany = (externalPlatformEnabledIds as string).split(',').includes(companyId);
  // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
  // const isRouteDisabled = useMatch(COMPANY_SETTINGS_ROUTE);
  const isCompanyClubContextEnabled = !!companyId && !companyId.includes(':') && !!clubSlug && !clubSlug.includes(':');

  const [companyClubState, setCompanyClubState] = useState<CompanyClubState>({
    id: '',
    legacyId: '',
    isImportLimited: false,
    isSiaeImportLimited: false,
  });

  const [companyState, setCompanyState] = useState<Company>({
    id: '',
    legacyId: '',
    name: '',
    commercialName: '',
    defaultChannel: {
      id: '',
      name: '',
      slug: '',
    },
  });

  const [clubState, setClubState] = useState<Club>({
    id: '',
    legacyId: '',
    coverUrl: '',
    logoUrl: '',
    name: '',
    slug: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
    city: {
      country: {
        id: '',
        name: '',
        isoCode: '',
        currency: '',
      },
      id: '',
      legacyId: '',
      coverUrl: '',
      name: '',
      slug: '',
      timezone: '',
    },
  });

  // Per-company gate for the interactive floorplan editor (V2), sourced from PostHog.
  // Derived here next to the sibling per-company flags so the combine rule lives in one place.
  const isFloorplanV2Active = shouldShowFloorplanV2(
    useFeatureFlag('floorplan-interactive-editor'),
    useFeatureFlagPayload('floorplan-interactive-editor'),
    companyState.legacyId
  );

  // Per-company gate for the new admission form (drawer/full-screen), sourced from PostHog. Derived here
  // next to the sibling per-company flags so the combine rule lives in one place.
  const isNewAdmissionFormActive = shouldShowNewForm(
    useFeatureFlag('new-admission-form'),
    useFeatureFlagPayload('new-admission-form'),
    companyState.legacyId
  );

  // Per-company gate for the new invitation form (B2B-1881) — an independent rollout switch from
  // admissions/passes, same fail-closed payload rule.
  const isNewInvitationFormActive = shouldShowNewForm(
    useFeatureFlag('new-invitation-form'),
    useFeatureFlagPayload('new-invitation-form'),
    companyState.legacyId
  );

  // Per-company gate for the new add-on form — an independent rollout switch from the admission
  // family, same fail-closed payload rule (requires the PostHog payload, e.g. `allCompanies: true`).
  const isNewAddonFormActive = shouldShowNewForm(
    useFeatureFlag('new-addon-form'),
    useFeatureFlagPayload('new-addon-form'),
    companyState.legacyId
  );

  // Gates the bottle-service "Extra guests" field — a simple on/off PostHog flag (no per-company payload).
  const isExtraGuestsForBottleServiceActive = useFeatureFlag('extra-guests-for-bottle-services');

  // Phase-2 gates for the new offer drawers: features held back from the 100% form rollout.
  // Simple on/off PostHog flags. The galleries replaced the build-time SSM params
  // `show-admission-image-gallery` / `show-addon-image-gallery`, which were global per environment.
  // The admission gallery flag also covers the pass and admission-preset galleries (same chassis).
  const isAdmissionImageGalleryActive = useFeatureFlag('admission-image-gallery');
  const isAddonImageGalleryActive = useFeatureFlag('addon-image-gallery');
  const isAdmissionIncludeDrinksActive = useFeatureFlag('admission-include-drinks');
  const isAddonTermsAndConditionsLinkActive = useFeatureFlag('addon-terms-and-conditions-link');

  // Gates the widget Checkout "Enable sign-in" section and the SIAE booking-plugin share link.
  // Derived once here so the flag key isn't read inline across consumers (widget form, admission card).
  const isWidgetSignInEnabled = useFeatureFlag('widget-config-enable-sign-in');

  // Gates the whole Secret Location UI (toggle, dropdowns, LocationCard). Targeting lives in PostHog
  // (company group + internal cohort), so no payload is read here — the flag arrives already resolved
  // for the current company and is fail-closed while it loads. Replaced VITE_SHOW_SECRET_LOCATION,
  // which was global per environment (B2B-2059).
  const isSecretLocationActive = useFeatureFlag('event-secret-location');

  const setEmptyCompanyClubState = useCallback(() => {
    setClubState(companyClubEmptyState.clubState);
    setCompanyClubState(companyClubEmptyState.companyClubState);
    setCompanyState(companyClubEmptyState.companyState);
  }, []);

  const {
    data: companyClub,
    error: companyClubError,
    isLoading: isLoadingCompanyClub,
  } = useGetCompanyClub({
    companyId,
    clubSlug,
    options: {
      // TODO: remove comment in isRouteDisabled to load CompanyClubContext if route is disabled
      enabled: isCompanyClubContextEnabled && !!userState, // && !isRouteDisabled,
    },
  });

  const shouldNavigateToSelectVenue =
    userState &&
    !isCompanyClubContextEnabled &&
    pathname !== SELECT_VENUE_ROUTE &&
    pathname !== USER_SETTINGS_ROUTE &&
    isSelectVenueActive;

  useEffect(() => {
    if (shouldNavigateToSelectVenue) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [shouldNavigateToSelectVenue, navigate]);

  useEffect(() => {
    if (!!companyClubError && companyClubError.status === 403 && isSelectVenueActive && isAuthenticated) {
      navigate(SELECT_VENUE_ROUTE);
    }
  }, [companyClubError, navigate, isSelectVenueActive, isAuthenticated]);

  useEffect(() => {
    if (companyClub && isCompanyClubContextEnabled && !companyClubError?.status) {
      const { id, legacyId, isImportLimited, isSiaeImportLimited } = companyClub;
      setCompanyClubState({
        id,
        legacyId,
        isImportLimited,
        isSiaeImportLimited,
      });
      if (companyClub?.club) setClubState(companyClub.club);
      if (companyClub?.company) {
        setCompanyState(companyClub.company);
        const legacyCompanyId = Number.parseInt(companyClub.company.legacyId);
        if (posthog && !isImpersonated && !userState?.isSuperAdmin && !Number.isNaN(legacyCompanyId)) {
          identifyCompanyGroupProperties(posthog, {
            companyId: legacyCompanyId,
            name: companyClub.company.name,
            commercialName: companyClub.company.commercialName,
          });
        }
      }
      setCompanyAuthId(companyClub.company.id);
      setCompanyClubId(companyClub.club.id);
    } else if (!isCompanyClubContextEnabled) {
      setEmptyCompanyClubState();
      if (posthog) clearCompanyContext(posthog);
    }
  }, [
    companyId,
    clubSlug,
    companyClub,
    isCompanyClubContextEnabled,
    setEmptyCompanyClubState,
    userState?.isSuperAdmin,
    isSelectVenueActive,
    companyClubError,
    setCompanyAuthId,
    setCompanyClubId,
    posthog,
    isImpersonated,
  ]);

  const memoizedContextValue = useMemo<CompanyClubContextValue>(
    () => ({
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isNewAdmissionFormActive,
      isNewInvitationFormActive,
      isNewAddonFormActive,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isSecretLocationActive,
      isLoadingCompanyClub,
    }),
    [
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isNewAdmissionFormActive,
      isNewInvitationFormActive,
      isNewAddonFormActive,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isSecretLocationActive,
      isLoadingCompanyClub,
    ]
  );

  return <CompanyClubContext.Provider value={memoizedContextValue}>{children}</CompanyClubContext.Provider>;
};
